import type React from "react";
import { useContext, useEffect, useRef, useState } from "react";

import { faPen } from "@fortawesome/pro-regular-svg-icons";
import { faCircleCheck, faCircleXmark } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box } from "@mui/material";
import { parseISO } from "date-fns";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";

import { useGetTreatmentActivityLogsPatient } from "api/hooks/useGetTreatmentActivityLogsPatient";
import { usePutTreatmentActivityLog } from "api/hooks/usePutTreatmentActivityLog";
import type { LogEntry } from "api/schemas/TreatmentActivityLog";
import { ButtonContainer } from "routes/admin-billing/components/SharedStyles";
import { BaseButton } from "shared/atoms/BaseButton";
import { MUIAccordion, MUIAccordionDetails, MUIAccordionSummary } from "shared/atoms/MUIAccordion";
import { Notification } from "shared/atoms/Notification";
import { Title } from "shared/molecules/MUIDataGrid/MUIDataGrid";
import { CurrentPatientContext } from "utils/contexts";
import { useLocalizedDate } from "utils/date";
import { reportError } from "utils/errorReporting";

import { ActivityLogConfirmDialog, type Dialog, initialDialogState } from "../components/ActivityLogConfirmDialog";
import { ActivityLogForm } from "../components/ActivityLogForm";

const scrollElementIntoView = (element: HTMLElement | null) => {
  if (!element) {
    return;
  }

  setTimeout(() => {
    element.scrollIntoView({ behavior: "smooth", block: "center" });
  }, 500);
};

const getAccordionItemId = (month: string) => `activity-log-accordion-${month}`;

export const ActivityLog: React.FC = () => {
  const { t } = useTranslation();
  const { format } = useLocalizedDate();
  const theme = useTheme();
  const { patient, patientError } = useContext(CurrentPatientContext);
  const [showAddActivityForm, setShowAddActivityForm] = useState(false);
  const [showDialog, setShowDialog] = useState<Dialog>(initialDialogState);
  const [selectedActivityLog, setSelectedActivityLog] = useState<LogEntry | null>(null);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const { data: activityLogsPatient, refetch } = useGetTreatmentActivityLogsPatient(patient.id);
  const { mutateAsync, isLoading } = usePutTreatmentActivityLog();

  const logs = activityLogsPatient?.logs || {};

  useEffect(() => {
    scrollElementIntoView(containerRef.current);
  }, []);

  const handleExpanded = (month: string, expanded: boolean) => {
    if (expanded) {
      const accordionItem = document.getElementById(getAccordionItemId(month));
      scrollElementIntoView(accordionItem);
    }
  };

  const updateActivityLog = async () => {
    try {
      if (showDialog.id && showDialog.status) {
        mutateAsync(
          { id: showDialog.id, data: { status: showDialog.status } },
          {
            onSuccess: () => {
              setShowDialog(initialDialogState);
              refetch();
            },
            onError: () => {
              setError(t("errors.generic"));
            },
          }
        );
      }
    } catch (e) {
      setError(t("errors.generic"));
      if (e instanceof Error || typeof e === "string") {
        reportError("AvailabilityForm.tsx", e);
      }
    }
  };

  if ((patient.ce_structure === "non_rtm" || patient.ce_structure === "invited_non_rtm") && patient.week < 2) {
    return (
      <Container>
        <Notification>{t("patients.activity_log.disabled")}</Notification>
      </Container>
    );
  }

  return patientError ? (
    <ErrorContainer>
      <Notification variant="danger" style={{ margin: "10px" }}>
        {t("errors.generic")}
      </Notification>
    </ErrorContainer>
  ) : (
    <Container ref={containerRef}>
      <TopContent>
        <Title>{t("patients.menu.activity_log")}</Title>
        <BaseButton
          text=""
          onClick={() => setShowAddActivityForm(true)}
          variant="tertiary"
          icon="circle-plus"
          iconPlacement="end"
          style={{ flexGrow: 0 }}
        />
      </TopContent>

      <div>
        {logs &&
          Object.keys(logs).length > 0 &&
          Object.keys(logs)?.map((month, idx) => {
            const totalDuration = logs[month]?.reduce((acc, { duration }) => acc + (duration || 0), 0) || 0;
            const pendingCount = logs[month]?.filter(activity => activity.status === "pending").length || 0;
            const isFirstRow = idx === 0;

            return (
              <MUIAccordion
                key={month}
                defaultExpanded={isFirstRow}
                onChange={(e, expanded) => handleExpanded(month, expanded)}
                id={getAccordionItemId(month)}
              >
                <MUIAccordionSummary>
                  <Box display="flex" alignItems="center" gap="8px">
                    {format(parseISO(month), "MMMM yyyy")}
                    {pendingCount > 0 && (
                      <PendingCount>
                        ({t("patients.activity_log.table.pending_count", { count: pendingCount })})
                      </PendingCount>
                    )}
                  </Box>
                </MUIAccordionSummary>
                <MUIAccordionDetails>
                  <List>
                    <ListItem>
                      <BrightCell>{t("patients.activity_log.table.date")}</BrightCell>
                      <DarkCell>{t("patients.activity_log.table.activity")}</DarkCell>
                      <BrightCell>{t("patients.activity_log.table.time")}</BrightCell>
                      <BrightCell>{t("patients.activity_log.table.accept")}</BrightCell>
                      <BrightCell>{t("patients.activity_log.table.discard")}</BrightCell>
                      <BrightCell>{t("patients.activity_log.table.edit")}</BrightCell>
                    </ListItem>
                    {logs[month]?.map(activity => {
                      const isEditable = activity.status === "pending";

                      return (
                        <ListItem key={activity.id}>
                          <BrightCell>{activity.start_time.slice(0, 10)}</BrightCell>
                          <DarkCell>{activity.category.description}</DarkCell>
                          <BrightCell>
                            {t("patients.activity_log.table.duration", { duration: activity.duration / 60 })}
                          </BrightCell>
                          <BrightCell>
                            {isEditable && (
                              <ButtonContainer
                                onClick={e => {
                                  e.stopPropagation();
                                  setShowDialog({ id: activity.id, show: true, status: "accepted" });
                                }}
                              >
                                <FontAwesomeIcon
                                  icon={faCircleCheck}
                                  color={theme.colors.redesign.g100}
                                  style={{ height: "18px", width: "18px" }}
                                />
                              </ButtonContainer>
                            )}
                          </BrightCell>
                          <BrightCell>
                            {isEditable && (
                              <ButtonContainer
                                onClick={e => {
                                  e.stopPropagation();
                                  setShowDialog({ id: activity.id, show: true, status: "discarded" });
                                }}
                              >
                                <FontAwesomeIcon
                                  icon={faCircleXmark}
                                  color={theme.colors.redesign.r100}
                                  style={{ height: "18px", width: "18px" }}
                                />
                              </ButtonContainer>
                            )}
                          </BrightCell>
                          <BrightCell>
                            {isEditable && (
                              <ButtonContainer
                                onClick={e => {
                                  e.stopPropagation();
                                  setSelectedActivityLog(activity);
                                  setShowAddActivityForm(true);
                                }}
                              >
                                <FontAwesomeIcon
                                  icon={faPen}
                                  color={theme.colors.black}
                                  style={{ height: "18px", width: "18px" }}
                                />
                              </ButtonContainer>
                            )}
                          </BrightCell>
                        </ListItem>
                      );
                    })}
                    <ListItem>
                      <DarkCell>{t("patients.activity_log.table.summary")}</DarkCell>
                      <DarkCell />
                      <DarkCell>
                        {t("patients.activity_log.table.duration", {
                          duration: totalDuration ? totalDuration / 60 : 0,
                        })}
                      </DarkCell>
                    </ListItem>
                  </List>
                </MUIAccordionDetails>
              </MUIAccordion>
            );
          })}
      </div>

      {showAddActivityForm && (
        <ActivityLogForm
          selectedActivityLog={selectedActivityLog}
          onClose={() => {
            setShowAddActivityForm(false);
            setSelectedActivityLog(null);
          }}
          refetch={refetch}
        />
      )}

      {showDialog.show && showDialog.id && showDialog.status && (
        <ActivityLogConfirmDialog
          isOpen
          status={showDialog.status}
          onConfirm={updateActivityLog}
          onCancel={() => setShowDialog(initialDialogState)}
          isLoading={isLoading}
          error={error}
        />
      )}
    </Container>
  );
};

const ErrorContainer = styled.div`
  margin: 0 auto ${props => props.theme.spacing.S_20};
  max-width: 1000px;
  padding: ${props => props.theme.spacing.S_20};
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
`;

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 20px;
`;

const TopContent = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 16px;
  gap: 16px;
`;

const PendingCount = styled.span`
  color: ${({ theme }) => theme.colors.redesign.db60};
  font-size: 14px;
`;

const List = styled.ul`
  display: flex;
  flex-flow: column nowrap;
  list-style: none;
  padding: 0;
  margin: 0;
`;

const ListItem = styled.li`
  display: flex;
  padding: 12px 16px;

  &:not(:last-of-type) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.redesign.db20};
  }
`;

const Cell = styled.div`
  display: flex;
  flex: 1;
  font-size: 14px;
`;

const BrightCell = styled(Cell)`
  color: ${({ theme }) => theme.colors.redesign.db60};
`;

const DarkCell = styled(Cell)`
  color: ${({ theme }) => theme.colors.redesign.db90};
`;
